class ProductCarouselManager {
  constructor() {
    this._splideLoadPromise = null;
    this._pendingElements = new Set();
    this._carouselObserver = null;
    this._boot = this._boot.bind(this);

    this._init(document);
    this._bindGlobalEvents();
  }

  _init(scope) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this._boot(scope));
    } else {
      this._boot(scope);
    }
  }

  _bindGlobalEvents() {
    if (typeof MutationObserver === 'undefined') return;

    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (!node || node.nodeType !== 1) return;

          if (node.matches?.('[data-product-carousel], [data-product-card-slider]')) {
            this._boot(node.parentNode || document);
            return;
          }
          if (node.querySelector?.('[data-product-carousel], [data-product-card-slider]')) {
            this._boot(node);
          }
        });
      });
    });

    if (document.body) {
      observer.observe(document.body, { childList: true, subtree: true });
    }

    document.addEventListener('shopify:section:load', () => this._boot(document));
  }

  _boot(scope) {
    const elements = this._getCarouselElements(scope);
    if (!elements.length) return;

    if (typeof IntersectionObserver === 'undefined') {
      this._ensureSplide(elements);
      return;
    }

    if (!this._carouselObserver) {
      this._carouselObserver = new IntersectionObserver(entries => {
        const visibleElements = entries
          .filter(entry => entry.isIntersecting)
          .map(entry => entry.target);

        visibleElements.forEach(element => this._carouselObserver.unobserve(element));
        if (visibleElements.length) this._ensureSplide(visibleElements);
      }, { rootMargin: '300px 0px' });
    }

    elements.forEach(element => {
      if (element.dataset.carouselReady === 'true' || element.dataset.sliderReady === 'true') return;
      this._carouselObserver.observe(element);
    });
  }

  loadSplide() {
    if (typeof window.Splide !== 'undefined') return Promise.resolve(window.Splide);
    if (typeof window.loadThemeSplide === 'function') return window.loadThemeSplide();
    if (this._splideLoadPromise) return this._splideLoadPromise;

    this._splideLoadPromise = new Promise((resolve, reject) => {
      const assetUrls = window.themeAssetUrls || {};
      if (!assetUrls.splideJs) {
        reject(new Error('Splide asset URL is unavailable.'));
        return;
      }

      if (!document.querySelector('link[href*="splide.min.css"]') && assetUrls.splideCss) {
        const cssLink = document.createElement('link');
        cssLink.rel = 'stylesheet';
        cssLink.href = assetUrls.splideCss;
        document.head.appendChild(cssLink);
      }

      const finish = () => {
        if (typeof window.Splide === 'undefined') {
          reject(new Error('Splide failed to load.'));
          return;
        }
        resolve(window.Splide);
      };

      const existingScript = document.querySelector('script[data-splide-loader], script[src*="splide.min.js"]');
      if (existingScript) {
        existingScript.addEventListener('load', finish, { once: true });
        existingScript.addEventListener('error', reject, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = assetUrls.splideJs;
      script.async = true;
      script.dataset.splideLoader = 'true';
      script.onload = finish;
      script.onerror = reject;
      document.body.appendChild(script);
    });

    return this._splideLoadPromise;
  }

  _ensureSplide(elements) {
    elements.forEach(element => this._pendingElements.add(element));

    this.loadSplide().then(() => {
      const pendingElements = Array.from(this._pendingElements);
      this._pendingElements.clear();
      this._mountAll(pendingElements);
    }).catch(() => {
      this._splideLoadPromise = null;
    });
  }

  _mountAll(scope) {
    if (typeof Splide === 'undefined') return;

    const elements = Array.isArray(scope) ? scope : this._getCarouselElements(scope);

    elements.forEach(el => {
      if (el.matches('[data-product-card-slider]')) {
        this._mountProductCard(el);
      } else {
        this._mountOne(el);
      }
    });
  }

  _getCarouselElements(scope) {
    const root = scope?.querySelectorAll ? scope : document;
    const elements = [];

    if (root.matches?.('[data-product-carousel], [data-product-card-slider]')) {
      elements.push(root);
    }

    root.querySelectorAll('[data-product-carousel], [data-product-card-slider]').forEach(el => {
      if (!elements.includes(el)) elements.push(el);
    });

    return elements.filter(element => element.querySelectorAll('.splide__slide').length > 1);
  }

  _mountOne(el) {
    if (el.dataset.carouselReady === 'true') return;
    el.dataset.carouselReady = 'true';

    const slides = el.querySelectorAll(':scope > .splide__track > .splide__list > .splide__slide');
    const gap = el.dataset.carouselGap ? `${el.dataset.carouselGap}px` : undefined;
    const peek = el.dataset.carouselPeek ? `${el.dataset.carouselPeek}px` : undefined;

    const instance = new Splide(el, {
      type: 'slide',
      perPage: 1,
      perMove: 1,
      arrows: false,
      pagination: slides.length > 1,
      drag: slides.length > 1,
      gap,
      padding: peek ? { right: peek } : undefined,
    });
    instance.mount();
  }

  _hydrateProductCardSlide(slide) {
    const image = slide?.querySelector('img[data-lazy-image]');
    if (!image) return;

    if (image.dataset.srcset) image.setAttribute('srcset', image.dataset.srcset);
    if (image.dataset.src) image.setAttribute('src', image.dataset.src);
    image.removeAttribute('data-srcset');
    image.removeAttribute('data-src');
    image.removeAttribute('data-lazy-image');
  }

  _mountProductCard(el) {
    if (el.dataset.sliderReady === 'true') return;

    const slideCount = el.querySelectorAll('.splide__slide').length;
    if (slideCount <= 1) {
      el.dataset.sliderReady = 'true';
      return;
    }

    const previousButton = el.querySelector('[data-product-card-prev]');
    const nextButton = el.querySelector('[data-product-card-next]');
    const slides = el.querySelectorAll('.splide__slide');
    const hydrateSlide = index => {
      if (index < 0 || index >= slides.length) return;
      this._hydrateProductCardSlide(slides[index]);
    };

    hydrateSlide(0);
    const instance = new Splide(el, {
      type: 'fade',
      rewind: true,
      perPage: 1,
      perMove: 1,
      arrows: false,
      pagination: false,
      drag: true,
      speed: 300,
    });

    instance.on('move', newIndex => {
      hydrateSlide(newIndex);
      hydrateSlide((newIndex + 1) % slides.length);
    });

    previousButton?.addEventListener('click', event => {
      event.preventDefault();
      event.stopPropagation();
      instance.go('<');
    });

    nextButton?.addEventListener('click', event => {
      event.preventDefault();
      event.stopPropagation();
      instance.go('>');
    });

    instance.mount();
    el.splide = instance;
    el.dataset.sliderReady = 'true';

    const card = el.closest('.productCard');
    card?.addEventListener('mouseenter', () => {
      if (instance.index === 0) {
        hydrateSlide(1);
        instance.go(1);
      }
    });
    card?.addEventListener('mouseleave', () => instance.go(0));
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.ProductCarouselManager = new ProductCarouselManager();
});
